import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Badge } from 'reactstrap';
import { ArrowLeft, Save, Check, X, LayoutGrid, Info, Utensils, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSiteStore } from '../../../stores/Assets/site';
import { useSpaceStore } from '../../../stores/Assets/space';
import { useMenuStore } from '../../../stores/Assets/menu';
import { useKitchenStore } from '../../../stores/Assets/kitchen';
import './ManageSpacesView.css'; // Reuse existing styles
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { User, UserCircle, ChefHat, ArrowRight, GripVertical, AlertTriangle } from 'lucide-react';

const THEME_COLOR = '#c59253';

const DndItem = ({ id, name, icon: Icon, isSelected, index, type, statusText, colorClass }) => (
    <Draggable draggableId={`${type}-${id}`} index={index}>
        {(provided, snapshot) => (
            <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className={`d-flex align-items-center gap-2 p-2 mb-2 rounded-3 border transition-all ${snapshot.isDragging ? 'shadow-lg bg-soft-primary border-primary' : 'bg-white'}`}
                style={{
                    ...provided.draggableProps.style,
                    cursor: 'grab'
                }}
            >
                <div className="text-muted"><GripVertical size={14} /></div>
                <div className={`p-1 rounded-2 ${colorClass || 'bg-light'}`}>
                    <Icon size={14} />
                </div>
                <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: '13px' }}>{name}</div>
                    {statusText && <div className="text-muted" style={{ fontSize: '10px' }}>{statusText}</div>}
                </div>
            </div>
        )}
    </Draggable>
);

const DndColumn = ({ title, id, items, icon: Icon, type, kitchens, currentKitchenId }) => (
    <div className="d-flex flex-column h-100 bg-light rounded-4 border overflow-hidden flex-basis-33">
        <div className="p-3 border-bottom bg-white d-flex align-items-center justify-content-between">
            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                <Icon size={18} className="text-muted" />
                {title}
            </h6>
            <Badge color="soft-dark" className="rounded-pill">{items.length}</Badge>
        </div>
        <Droppable droppableId={id}>
            {(provided, snapshot) => (
                <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`p-2 flex-grow-1 overflow-auto custom-scroll transition-all ${snapshot.isDraggingOver ? 'bg-soft-primary' : ''}`}
                    style={{ minHeight: '300px' }}
                >
                    {items.map((item, index) => {
                        let statusText = null;
                        let colorClass = null;

                        if (id === 'elsewhere') {
                            const otherK = kitchens.find(k => k.kitchen_id !== currentKitchenId && (type === 'space' ? k.space_list?.some(sid => String(sid) === String(item.id)) : k.menu_list?.some(mid => String(mid) === String(item.id))));
                            statusText = otherK ? `In ${otherK.name}` : null;
                        }

                        return (
                            <DndItem
                                key={`${type}-${item.id}`}
                                id={item.id}
                                name={item.name}
                                icon={type === 'space' ? MapPin : Utensils}
                                index={index}
                                type={type}
                                statusText={statusText}
                                colorClass={id === 'current' ? 'bg-soft-primary text-primary' : null}
                            />
                        );
                    })}
                    {provided.placeholder}
                    {items.length === 0 && (
                        <div className="h-100 d-flex flex-column align-items-center justify-content-center opacity-25 p-4 text-center">
                            <Icon size={32} className="mb-2" />
                            <span className="small">Drop Here</span>
                        </div>
                    )}
                </div>
            )}
        </Droppable>
    </div>
);

const SimulationView = ({ kitchenInfo, allKitchens, allSpaces, allMenus, selectedSpaces, selectedMenus, onBack, onSave, saving }) => {
    const { t } = useTranslation();
    const [testSpace, setTestSpace] = useState(selectedSpaces[0] || (allSpaces[0]?.space_id));
    const [testMenu, setTestMenu] = useState(allMenus[0]?.menu_id);

    const getRecipient = (spaceId, menuId) => {
        // Priority 1: Menu Rule
        const menuKitchen = allKitchens.find(k =>
            k.menu_list?.some(id => String(id) === String(menuId)) ||
            (k.kitchen_id === kitchenInfo.kitchen_id && selectedMenus.some(id => String(id) === String(menuId)))
        );
        if (menuKitchen) return { name: menuKitchen.name, reason: t('Priority Menu Rule'), isPriority: true };

        // Priority 2: Space Link
        const spaceKitchen = allKitchens.find(k =>
            k.space_list?.some(id => String(id) === String(spaceId)) ||
            (k.kitchen_id === kitchenInfo.kitchen_id && selectedSpaces.some(id => String(id) === String(spaceId)))
        );
        if (spaceKitchen) return { name: spaceKitchen.name, reason: t('Area Link'), isPriority: false };

        return { name: t('Unassigned'), reason: t('No Rule Found'), isNone: true };
    };

    const recipient = getRecipient(testSpace, testMenu);
    const selectedSpaceObj = allSpaces.find(s => s.space_id === testSpace);
    const selectedMenuObj = allMenus.find(m => m.menu_id === testMenu);

    return (
        <div className="h-100 d-flex flex-column bg-white rounded-4 overflow-hidden">
            <div className="p-4 border-bottom bg-light">
                <div className="d-flex align-items-center gap-3 mb-3">
                    <Button color="link" className="text-muted p-0" onClick={onBack}><ArrowLeft size={20} /></Button>
                    <h5 className="fw-bold mb-0">{t('Step 3: Test Your Workflow')}</h5>
                </div>
                <p className="text-muted small mb-0">{t('Simulate an order to see exactly where it will land in your kitchen.')}</p>
            </div>

            <div className="flex-grow-1 p-4 d-flex flex-column align-items-center justify-content-center bg-body-theme overflow-auto">
                <div className="w-100 max-w-4xl bg-white rounded-4 shadow-lg p-5 border position-relative overflow-hidden">
                    <div className="row g-5 align-items-center">
                        {/* Source Side */}
                        <div className="col-md-5">
                            <div className="p-4 bg-light rounded-4 border text-center shadow-sm">
                                <UserCircle size={64} className="text-muted mb-3" />
                                <h6 className="fw-bold mb-3">{t('A Customer Orders')}</h6>

                                <div className="d-flex flex-column gap-3 text-start">
                                    <div>
                                        <label className="small fw-bold text-muted mb-1">{t('Sitting At')}</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={testSpace}
                                            onChange={(e) => setTestSpace(e.target.value)}
                                        >
                                            {allSpaces.map(s => <option key={s.space_id} value={s.space_id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="small fw-bold text-muted mb-1">{t('Ordering From')}</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={testMenu}
                                            onChange={(e) => setTestMenu(e.target.value)}
                                        >
                                            {allMenus.map(m => <option key={m.menu_id} value={m.menu_id}>{m.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Magic Arrow */}
                        <div className="col-md-2 d-none d-md-flex align-items-center justify-content-center">
                            <motion.div
                                animate={{ x: [0, 10, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                            >
                                <ArrowRight size={48} className="text-primary opacity-50" />
                            </motion.div>
                        </div>

                        {/* Destination Side */}
                        <div className="col-md-5">
                            <motion.div
                                key={recipient.name}
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className={`p-4 rounded-4 border text-center shadow-sm ${recipient.isNone ? 'bg-soft-danger' : 'bg-soft-primary'}`}
                                style={{ borderColor: recipient.isNone ? '#f8d7da' : THEME_COLOR }}
                            >
                                <ChefHat size={64} className="mb-3" style={{ color: recipient.isNone ? '#dc3545' : THEME_COLOR }} />
                                <h6 className="fw-bold mb-1">{t('Goes to Station')}</h6>
                                <h4 className="fw-bold mb-3" style={{ color: recipient.isNone ? '#dc3545' : THEME_COLOR }}>{recipient.name}</h4>

                                <div className="p-2 px-3 rounded-pill bg-white d-inline-flex align-items-center gap-2 border">
                                    {recipient.isPriority ? <AlertTriangle size={14} className="text-warning" /> : <MapPin size={14} className="text-info" />}
                                    <span className="small fw-bold">{recipient.reason}</span>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 border-top bg-light">
                <Button
                    block
                    size="lg"
                    disabled={saving}
                    className="w-100 rounded-pill py-3 shadow-lg fw-bold text-white border-0"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={onSave}
                >
                    {saving ? <Spinner size="sm" /> : t('Confirm & Save Station Flow')}
                </Button>
            </div>
        </div>
    );
};

const DndSelectionView = ({ onBack, onConfirm, siteName, items, selectedIds, kitchens, currentKitchenId, type }) => {
    const { t } = useTranslation();

    // Categorize items accurately using string normalization
    const current = items.filter(i => selectedIds.some(id => String(id) === String(i.id)));

    const sharedElsewhere = items.filter(i => {
        if (selectedIds.some(id => String(id) === String(i.id))) return false;
        return kitchens.some(k => k.kitchen_id !== currentKitchenId && (type === 'space' ? k.space_list?.some(sid => String(sid) === String(i.id)) : k.menu_list?.some(mid => String(mid) === String(i.id))));
    });

    const available = items.filter(i => {
        if (selectedIds.some(id => String(id) === String(i.id))) return false;
        const linkedElsewhere = kitchens.some(k => k.kitchen_id !== currentKitchenId && (type === 'space' ? k.space_list?.some(sid => String(sid) === String(i.id)) : k.menu_list?.some(mid => String(mid) === String(i.id))));
        return !linkedElsewhere;
    });

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId) return;

        const itemId = draggableId.split('-')[1];

        if (destination.droppableId === 'current') {
            const alreadyExists = selectedIds.some(id => String(id) === String(itemId));
            if (!alreadyExists) {
                const originalItem = items.find(i => String(i.id) === String(itemId));
                onConfirm([...selectedIds, originalItem ? originalItem.id : itemId], false);
            }
        } else {
            onConfirm(selectedIds.filter(id => String(id) !== String(itemId)), false);
        }
    };

    return (
        <div className="h-100 d-flex flex-column bg-white rounded-4 overflow-hidden">
            <div className="p-4 bg-light border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <Button color="link" className="text-muted p-0" onClick={onBack}><ArrowLeft size={20} /></Button>
                    <div>
                        <h5 className="fw-bold mb-0">{type === 'space' ? t('Step 1: Dining Area Assignment') : t('Step 2: Special Menu Rules')}</h5>
                        <p className="text-muted small mb-0">{type === 'space' ? t('Drag areas into your station box.') : t('Drag menus into your station box for priority routing.')}</p>
                    </div>
                </div>
            </div>

            <div className="flex-grow-1 p-4 overflow-hidden">
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="d-flex flex-column flex-md-row gap-3 h-100">
                        <DndColumn
                            id="available"
                            title={t('Available')}
                            items={available}
                            icon={LayoutGrid}
                            type={type}
                        />
                        <DndColumn
                            id="elsewhere"
                            title={t('Linked Elsewhere')}
                            items={sharedElsewhere}
                            icon={Info}
                            type={type}
                            kitchens={kitchens}
                            currentKitchenId={currentKitchenId}
                        />
                        <DndColumn
                            id="current"
                            title={t('Your Station')}
                            items={current}
                            icon={ChefHat}
                            type={type}
                        />
                    </div>
                </DragDropContext>
            </div>

            <div className="p-4 border-top bg-light">
                <Button
                    block
                    size="lg"
                    className="w-100 rounded-pill py-3 shadow-lg fw-bold text-white border-0"
                    style={{ backgroundColor: THEME_COLOR }}
                    onClick={() => onConfirm(selectedIds, true)}
                >
                    {t('Continue')}
                </Button>
            </div>
        </div>
    );
};

const ManageStationView = ({ siteId, kitchenId, onClose }) => {
    const { t } = useTranslation();
    const { sites } = useSiteStore();
    const { getSpaces } = useSpaceStore();
    const { getSiteMenuFull } = useMenuStore();
    const { getKitchensBySite, updateKitchen } = useKitchenStore();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState('spaces'); // 'spaces' | 'menus'

    const [siteInfo, setSiteInfo] = useState(null);
    const [kitchenInfo, setKitchenInfo] = useState(null);
    const [allSpaces, setAllSpaces] = useState([]);
    const [allMenus, setAllMenus] = useState([]);
    const [allKitchens, setAllKitchens] = useState([]);

    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [selectedMenus, setSelectedMenus] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const site = sites.find(s => s.site_id === siteId);
                setSiteInfo(site);

                const [spaces, menuData, kitchenData] = await Promise.all([
                    getSpaces(siteId, { page: 1, perPage: 100 }),
                    getSiteMenuFull(siteId),
                    getKitchensBySite(siteId, { page: 1, perPage: 100 })
                ]);

                setAllSpaces(spaces || []);
                setAllMenus(menuData?.menus || []);
                setAllKitchens(kitchenData?.kitchens || []);

                const currentKitchen = kitchenData?.kitchens?.find(k => k.kitchen_id === kitchenId);
                if (currentKitchen) {
                    setKitchenInfo(currentKitchen);
                    setSelectedSpaces(currentKitchen.space_list || []);
                    setSelectedMenus(currentKitchen.menu_list || []);
                }
            } catch (error) {
                console.error(error);
                toast.error(t('Failed to load station data'));
            } finally {
                setLoading(false);
            }
        };

        if (siteId && kitchenId) loadData();
    }, [siteId, kitchenId, sites]);

    const handleSave = async (menuIds) => {
        setSaving(true);
        try {
            // 1. Identify which spaces were moved from other kitchens
            const movedSpaces = selectedSpaces.filter(spaceId =>
                allKitchens.some(k => k.kitchen_id !== kitchenId && k.space_list?.some(sid => String(sid) === String(spaceId)))
            );

            // 2. Prepare updates for other kitchens (remove these spaces from their lists)
            if (movedSpaces.length > 0) {
                const otherKitchensToUpdate = allKitchens.filter(k =>
                    k.kitchen_id !== kitchenId &&
                    k.space_list?.some(id => movedSpaces.some(ms => String(ms) === String(id)))
                );

                for (const k of otherKitchensToUpdate) {
                    const updatedList = k.space_list.filter(id => !movedSpaces.some(ms => String(ms) === String(id)));
                    await updateKitchen(k.kitchen_id, { space_list: updatedList });
                }
            }
            // 3. Update the current kitchen
            const success = await updateKitchen(kitchenId, {
                space_list: selectedSpaces,
                menu_list: menuIds
            });

            if (success) {
                toast.success(t('Station configured successfully'));
                onClose();
            } else {
                toast.error(t('Failed to update station'));
            }
        } catch (error) {
            console.error(error);
            toast.error(t('An error occurred'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-body-theme d-flex align-items-center justify-content-center manage-spaces-overlay" style={{ zIndex: 1050 }}>
                <Spinner color="primary" style={{ color: THEME_COLOR }} />
            </div>
        );
    }

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-body-theme d-flex flex-column manage-spaces-overlay" style={{ zIndex: 1050 }}>
            {/* --- Header --- */}
            <div className="manage-spaces-header px-4 py-3 d-flex align-items-center justify-content-between shadow-sm">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={onClose} className="btn btn-light rounded-circle p-2 hover-bg-silver text-muted border">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h5 className="mb-0 fw-bold">{t('Manage Station')}: {kitchenInfo?.name}</h5>
                        <p className="text-muted small mb-0">{siteInfo?.name}</p>
                    </div>
                </div>
            </div>

            {/* --- Main Content --- */}
            <div className="flex-grow-1 overflow-hidden bg-body-theme position-relative">
                <AnimatePresence mode="wait">
                    {viewMode === 'spaces' && (
                        <motion.div
                            key="spaces"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="h-100 p-4"
                        >
                            <div className="h-100 max-w-6xl mx-auto shadow-lg rounded-4 overflow-hidden">
                                <DndSelectionView
                                    type="space"
                                    siteName={siteInfo?.name}
                                    items={allSpaces.map(s => ({ id: s.space_id, name: s.name }))}
                                    selectedIds={selectedSpaces}
                                    kitchens={allKitchens}
                                    currentKitchenId={kitchenId}
                                    onBack={onClose}
                                    onConfirm={(ids, next) => {
                                        setSelectedSpaces(ids);
                                        if (next) setViewMode('menus');
                                    }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {viewMode === 'menus' && (
                        <motion.div
                            key="menus"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="h-100 p-4"
                        >
                            <div className="h-100 max-w-6xl mx-auto shadow-lg rounded-4 overflow-hidden">
                                <DndSelectionView
                                    type="menu"
                                    siteName={siteInfo?.name}
                                    items={allMenus.map(m => ({ id: m.menu_id, name: m.name }))}
                                    selectedIds={selectedMenus}
                                    kitchens={allKitchens}
                                    currentKitchenId={kitchenId}
                                    onBack={() => setViewMode('spaces')}
                                    onConfirm={(ids, next) => {
                                        setSelectedMenus(ids);
                                        if (next) setViewMode('simulation');
                                    }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {viewMode === 'simulation' && (
                        <motion.div
                            key="simulation"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="h-100 p-4"
                        >
                            <div className="h-100 max-w-6xl mx-auto shadow-lg rounded-4 overflow-hidden">
                                <SimulationView
                                    kitchenInfo={kitchenInfo}
                                    allKitchens={allKitchens}
                                    allSpaces={allSpaces}
                                    allMenus={allMenus}
                                    selectedSpaces={selectedSpaces}
                                    selectedMenus={selectedMenus}
                                    onBack={() => setViewMode('menus')}
                                    onSave={() => handleSave(selectedMenus)}
                                    saving={saving}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <style>{`
                .flex-basis-33 { flex-basis: 33.333%; }
                .bg-soft-primary { background-color: rgba(197, 146, 83, 0.1) !important; }
                .bg-soft-danger { background-color: rgba(220, 53, 69, 0.1) !important; }
                .text-primary { color: #c59253 !important; }
                .border-primary { border-color: #c59253 !important; }
                @media (max-width: 768px) {
                    .flex-basis-33 { flex-basis: 100%; }
                }
            `}</style>

            {saving && (
                <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center" style={{ zIndex: 1100 }}>
                    <div className="text-center">
                        <Spinner color="primary" />
                        <p className="mt-2 fw-bold" style={{ color: THEME_COLOR }}>{t('Saving Configuration...')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageStationView;
